import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'components/Button';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import {
  OrderEditingActionMode,
  OrderEditingAnalytics,
  OrderEditingRequest,
  OrderEditingSettings,
  OrderEditingWorkspace,
  OrderEditingCatalogLoader,
} from 'types/order-editing';
import ApprovedProductPicker from './ApprovedProductPicker';
import {
  actionLabel,
  EXPANDED_ACTIONS,
  normalizeOrderEditingSettings,
} from './expandedSettings';
import {
  cardClass,
  displayDate,
  inputClass,
  money,
  orderEditingFee,
  SettingToggle,
  Status,
} from './WorkspaceSections';

const newId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

type SettingsEditorProps = {
  draft: OrderEditingSettings;
  update: (patch: Partial<OrderEditingSettings>) => void;
  disabled: boolean;
};

export function ExpandedSettings({
  draft,
  update,
  setup,
  disabled,
  loadCatalog,
}: SettingsEditorProps & {
  setup: OrderEditingWorkspace['setup'];
  loadCatalog?: OrderEditingCatalogLoader;
}) {
  const { expanded, catalog } = normalizeOrderEditingSettings(draft);
  return (
    <section
      id="order-edit-expanded"
      className={cardClass}
      aria-labelledby="expanded-heading"
    >
      <h2 id="expanded-heading" className="text-base font-semibold">
        Expanded edits
      </h2>
      <p className="mt-2 text-sm text-gray-600">
        Choose which changes customers can make or request for approval.
      </p>
      <p className="mt-3 rounded-md bg-gray-50 p-3 text-xs leading-5 text-gray-700">
        Physical quantity increases, additions and swaps need merchant approval
        and a fulfillment pause, even with Self-service selected.
      </p>
      <details className="mt-3 text-xs leading-5 text-gray-600">
        <summary className="cursor-pointer font-medium text-gray-700">
          How approvals and payments work
        </summary>
        <div className="mt-2 space-y-2">
          <p>
            Approvals stay open until the original editing deadline. After
            approval, the customer must confirm a fresh quote within 10 minutes
            and before that deadline. Approval does not edit the order or
            collect payment. Quantity reductions and removals follow the mode
            you choose.
          </p>
          <p>
            Priced changes require a supported, fully unfulfilled prepaid order
            in one currency. Payment due is collected through Shopify; refunds
            due require merchant settlement. Unsupported orders can use your
            support fallback.
          </p>
          <p>
            All completed changes share the same{' '}
            {orderEditingFee(setup.billing?.priceCents)} once-per-order fee.
          </p>
        </div>
      </details>
      <fieldset
        disabled={disabled}
        className="mt-4 grid gap-x-8 md:grid-cols-2"
      >
        {EXPANDED_ACTIONS.filter(({ key }) => key !== 'shippingUpgrade').map(
          ({ key, label, description }) => {
            const capability = setup.capabilities?.[key];
            return (
              <div key={key} className="border-b border-gray-100 py-4">
                <label
                  htmlFor={`expanded-${key}`}
                  className="text-sm font-medium"
                >
                  {label}
                </label>
                <select
                  id={`expanded-${key}`}
                  className={inputClass}
                  value={expanded[key]}
                  onChange={(event) =>
                    update({
                      expanded: {
                        ...expanded,
                        [key]: event.target.value as OrderEditingActionMode,
                      },
                    })
                  }
                >
                  <option value="off">Off</option>
                  <option
                    value="self_service"
                    disabled={
                      !!capability?.supportedModes &&
                      !capability.supportedModes.includes('self_service')
                    }
                  >
                    Self-service
                  </option>
                  <option
                    value="approval"
                    disabled={
                      !!capability?.supportedModes &&
                      !capability.supportedModes.includes('approval')
                    }
                  >
                    Approval required
                  </option>
                </select>
                <div className="mt-2">
                  <Status value={capability?.status || 'unknown'} />
                </div>
                <details className="mt-2 text-xs leading-5 text-gray-500">
                  <summary
                    aria-label={`Details for ${label}`}
                    className="cursor-pointer font-medium text-gray-600"
                  >
                    Details
                  </summary>
                  <p className="mt-2">{description}</p>
                  <p className="mt-1">
                    {capability?.detail ||
                      'Readiness has not been reported. The server checks each order before allowing changes.'}
                  </p>
                </details>
              </div>
            );
          },
        )}
      </fieldset>
      <details className="mt-5 border-t border-gray-200 pt-4">
        <summary className="cursor-pointer text-sm font-medium">
          Product choices & limits
        </summary>
        <ApprovedProductPicker
          selectedIds={catalog.approvedVariantIds}
          onChange={(approvedVariantIds) =>
            update({ catalog: { ...catalog, approvedVariantIds } })
          }
          loadCatalog={loadCatalog}
          disabled={disabled}
        />
        <fieldset disabled={disabled} className="mt-5">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium">
              Maximum quantity per line
              <NonNegativeNumberInput
                className={inputClass}
                min={1}
                max={999}
                step={1}
                value={
                  Number.isFinite(catalog.maxQuantityPerLine)
                    ? catalog.maxQuantityPerLine
                    : ''
                }
                onChange={(event) =>
                  update({
                    catalog: {
                      ...catalog,
                      maxQuantityPerLine:
                        event.target.value === ''
                          ? NaN
                          : Number(event.target.value),
                    },
                  })
                }
              />
            </label>
            <label className="block text-sm font-medium">
              Maximum added lines
              <NonNegativeNumberInput
                className={inputClass}
                min={1}
                max={20}
                step={1}
                value={
                  Number.isFinite(catalog.maxAddedLines)
                    ? catalog.maxAddedLines
                    : ''
                }
                onChange={(event) =>
                  update({
                    catalog: {
                      ...catalog,
                      maxAddedLines:
                        event.target.value === ''
                          ? NaN
                          : Number(event.target.value),
                    },
                  })
                }
              />
            </label>
          </div>
        </fieldset>
      </details>
    </section>
  );
}

export function ShippingOfferSettings({
  draft,
  update,
  disabled,
}: SettingsEditorProps) {
  const { shippingOffers } = normalizeOrderEditingSettings(draft);
  return (
    <section className={cardClass} aria-labelledby="shipping-offers-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="shipping-offers-heading" className="text-base font-semibold">
          Shipping offer configuration
        </h2>
        <Button
          variant="outlined"
          disabled={disabled || shippingOffers.length >= 10}
          onClick={() =>
            update({
              shippingOffers: [
                ...shippingOffers,
                {
                  id: newId('shipping'),
                  title: '',
                  amount: '',
                  currency: 'USD',
                  countries: [],
                  carrier: '',
                  serviceCode: '',
                  maxWeightGrams: null,
                },
              ],
            })
          }
        >
          Add shipping offer
        </Button>
      </div>
      <p className="mt-2 text-sm text-gray-600">
        Store-managed negotiated offers. These are configured prices and
        fulfillment service mappings, not live carrier quotes. They do not
        change Shopify’s delivery method or purchase labels.
      </p>
      <p className="mt-2 text-xs text-gray-600">
        Use the Shopify fulfillment service ID, or manual for merchant-managed
        fulfillment, in the carrier field. The service code must be recognized
        by your warehouse or fulfillment integration.
      </p>
      {!shippingOffers.length && (
        <p className="mt-4 text-sm text-gray-500">
          No shipping offers configured. Shipping offers remain off until you
          enable the action and save.
        </p>
      )}
      <fieldset disabled={disabled} className="mt-4 space-y-4">
        {shippingOffers.map((offer, index) => {
          const edit = (patch: Partial<typeof offer>) =>
            update({
              shippingOffers: shippingOffers.map((item) =>
                item.id === offer.id ? { ...item, ...patch } : item,
              ),
            });
          const label = `Shipping offer ${index + 1}`;
          return (
            <div
              key={offer.id}
              className="rounded-md border border-gray-200 p-4"
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-medium">Offer {index + 1}</h3>
                <Button
                  variant="outlined"
                  onClick={() =>
                    update({
                      shippingOffers: shippingOffers.filter(
                        (item) => item.id !== offer.id,
                      ),
                    })
                  }
                >
                  Remove shipping offer {index + 1}
                </Button>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <label className="text-sm">
                  {label} title
                  <input
                    className={inputClass}
                    maxLength={120}
                    value={offer.title}
                    onChange={(event) => edit({ title: event.target.value })}
                  />
                </label>
                <label className="text-sm">
                  {label} amount
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    placeholder="12.50"
                    value={offer.amount}
                    onChange={(event) => edit({ amount: event.target.value })}
                  />
                </label>
                <label className="text-sm">
                  {label} currency
                  <input
                    className={inputClass}
                    maxLength={3}
                    value={offer.currency}
                    onChange={(event) =>
                      edit({ currency: event.target.value.toUpperCase() })
                    }
                  />
                </label>
                <label className="text-sm">
                  {label} countries
                  <input
                    className={inputClass}
                    placeholder="US, CA"
                    value={offer.countries.join(', ')}
                    onChange={(event) =>
                      edit({
                        countries: event.target.value
                          .split(',')
                          .map((value) => value.trim().toUpperCase()),
                      })
                    }
                  />
                </label>
                <label className="text-sm">
                  {label} carrier
                  <input
                    className={inputClass}
                    maxLength={80}
                    value={offer.carrier}
                    onChange={(event) => edit({ carrier: event.target.value })}
                  />
                </label>
                <label className="text-sm">
                  {label} service code
                  <input
                    className={inputClass}
                    maxLength={80}
                    value={offer.serviceCode}
                    onChange={(event) =>
                      edit({ serviceCode: event.target.value })
                    }
                  />
                </label>
                <label className="text-sm">
                  {label} maximum weight (grams)
                  <NonNegativeNumberInput
                    className={inputClass}
                    min={1}
                    step={1}
                    placeholder="No weight limit"
                    max={1000000000}
                    value={
                      offer.maxWeightGrams === null ||
                      offer.maxWeightGrams === undefined
                        ? ''
                        : offer.maxWeightGrams
                    }
                    onChange={(event) =>
                      edit({
                        maxWeightGrams:
                          event.target.value === ''
                            ? null
                            : Number(event.target.value),
                      })
                    }
                  />
                </label>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Amounts remain exact decimal values; the provider validates
                currency precision. Choose eligible country codes. Leave weight
                empty for no weight limit.
              </p>
            </div>
          );
        })}
      </fieldset>
    </section>
  );
}

function SingleLineMessage({
  label,
  value,
  maxLength,
  onChange,
  className,
}: {
  label: string;
  value: string;
  maxLength: number;
  onChange: (value: string) => void;
  className?: string;
}) {
  const [pasteError, setPasteError] = useState(false);
  const id = `order-edit-${label.toLowerCase().replace(/\W+/g, '-')}`;
  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        type="text"
        className={inputClass}
        maxLength={maxLength}
        value={value}
        aria-describedby={`${id}-help${pasteError ? ` ${id}-error` : ''}`}
        aria-invalid={pasteError || undefined}
        onChange={(event) => {
          setPasteError(false);
          onChange(event.target.value);
        }}
        onPaste={(event) => {
          if (/[\r\n]/.test(event.clipboardData.getData('text'))) {
            event.preventDefault();
            setPasteError(true);
          } else {
            setPasteError(false);
          }
        }}
      />
      <p id={`${id}-help`} className="mt-1 text-xs text-gray-500">
        Use plain text on one line, up to {maxLength} characters.
      </p>
      {pasteError && (
        <p
          id={`${id}-error`}
          role="alert"
          className="mt-1 text-xs text-red-700"
        >
          Use a single line for this message. The pasted text was not added.
        </p>
      )}
    </div>
  );
}

export function SupportCancellationSettings({
  draft,
  update,
  disabled,
}: SettingsEditorProps) {
  const { support, cancellation } = normalizeOrderEditingSettings(draft);
  return (
    <section
      id="order-edit-support"
      className={cardClass}
      aria-labelledby="support-heading"
    >
      <details>
        <summary className="cursor-pointer">
          <h2 id="support-heading" className="inline text-base font-semibold">
            Support & cancellation
          </h2>
        </summary>
        <fieldset
          disabled={disabled}
          className="mt-3 grid gap-6 lg:grid-cols-2"
        >
          <div>
            <SettingToggle
              label="Offer support when an edit cannot complete"
              description="Customers can submit a support request instead of repeatedly retrying an unsupported change."
              value={support.enabled}
              disabled={disabled}
              onChange={(enabled) =>
                update({ support: { ...support, enabled } })
              }
            />
            <label className="mt-3 block text-sm font-medium">
              Support contact URL
              <input
                className={inputClass}
                type="url"
                placeholder="https://your-store.example/pages/contact"
                maxLength={1000}
                value={support.contactUrl}
                onChange={(event) =>
                  update({
                    support: { ...support, contactUrl: event.target.value },
                  })
                }
              />
            </label>
            <SingleLineMessage
              className="mt-3"
              label="Support message"
              maxLength={1000}
              value={support.message}
              onChange={(message) =>
                update({ support: { ...support, message } })
              }
            />
          </div>
          <div>
            <SettingToggle
              label="Require a cancellation reason"
              value={cancellation.reasonRequired}
              disabled={disabled}
              onChange={(reasonRequired) =>
                update({ cancellation: { ...cancellation, reasonRequired } })
              }
            />
            <label className="mt-3 block text-sm font-medium">
              Cancellation reasons
              <textarea
                aria-label="Cancellation reasons"
                className={inputClass}
                rows={4}
                placeholder="One reason per line"
                value={cancellation.reasons.join('\n')}
                onChange={(event) =>
                  update({
                    cancellation: {
                      ...cancellation,
                      reasons: event.target.value.split(/\r?\n/),
                    },
                  })
                }
              />
              <span className="mt-1 block text-xs font-normal text-gray-500">
                Up to 10 reasons, 120 characters each.
              </span>
            </label>
            <SettingToggle
              label="Offer an incentive to keep the order"
              value={cancellation.retentionEnabled}
              disabled={disabled}
              onChange={(retentionEnabled) =>
                update({ cancellation: { ...cancellation, retentionEnabled } })
              }
            />
          </div>
        </fieldset>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Retention offers</h3>
          <Button
            variant="outlined"
            disabled={disabled || cancellation.retentionOffers.length >= 10}
            onClick={() =>
              update({
                cancellation: {
                  ...cancellation,
                  retentionOffers: [
                    ...cancellation.retentionOffers,
                    {
                      id: newId('retention'),
                      title: '',
                      message: '',
                      code: '',
                    },
                  ],
                },
              })
            }
          >
            Add retention offer
          </Button>
        </div>
        <p className="mt-2 text-xs text-gray-600">
          Use an existing coupon for a future purchase. Accepting an offer does
          not reduce the current order total, create a refund or incur an
          editing fee.
        </p>
        <fieldset disabled={disabled} className="mt-3 space-y-4">
          {cancellation.retentionOffers.map((offer, index) => {
            const edit = (patch: Partial<typeof offer>) =>
              update({
                cancellation: {
                  ...cancellation,
                  retentionOffers: cancellation.retentionOffers.map((item) =>
                    item.id === offer.id ? { ...item, ...patch } : item,
                  ),
                },
              });
            const label = `Retention offer ${index + 1}`;
            return (
              <div
                key={offer.id}
                className="rounded-md border border-gray-200 p-4"
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-sm">
                    {label} title
                    <input
                      className={inputClass}
                      maxLength={120}
                      value={offer.title}
                      onChange={(event) => edit({ title: event.target.value })}
                    />
                  </label>
                  <label className="text-sm">
                    {label} coupon code
                    <input
                      className={inputClass}
                      maxLength={100}
                      value={offer.code}
                      onChange={(event) => edit({ code: event.target.value })}
                    />
                  </label>
                  <SingleLineMessage
                    className="sm:col-span-2"
                    label={`${label} message`}
                    maxLength={500}
                    value={offer.message}
                    onChange={(message) => edit({ message })}
                  />
                </div>
                <div className="mt-3">
                  <Button
                    variant="outlined"
                    onClick={() =>
                      update({
                        cancellation: {
                          ...cancellation,
                          retentionOffers: cancellation.retentionOffers.filter(
                            (item) => item.id !== offer.id,
                          ),
                        },
                      })
                    }
                  >
                    Remove retention offer {index + 1}
                  </Button>
                </div>
              </div>
            );
          })}
        </fieldset>
      </details>
    </section>
  );
}

export function ChangeSummary({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '')
    return <p className="text-gray-500">Not recorded</p>;
  if (typeof value === 'string' || typeof value === 'number')
    return <p className="break-words">{String(value)}</p>;
  return (
    <pre className="whitespace-pre-wrap break-words text-xs">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export function ReviewQueue({
  requests,
  busy,
  reviewing,
  onReview,
  onDirtyChange,
}: {
  requests: OrderEditingRequest[];
  busy: boolean;
  reviewing?: string;
  onReview: (
    requestId: string,
    decision: 'approve' | 'reject',
    note: string,
    fulfillmentPaused?: boolean,
  ) => Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [paused, setPaused] = useState<Record<string, boolean>>({});
  const pending = useMemo(
    () =>
      requests.filter((request) =>
        ['awaiting_approval', 'open'].includes(request.status),
      ),
    [requests],
  );
  const dirty = pending.some(
    (request) => !!notes[request.id] || !!paused[request.id],
  );
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => {
    const pendingIds = new Set(pending.map((request) => request.id));
    setNotes((previous) =>
      Object.keys(previous).every((id) => pendingIds.has(id))
        ? previous
        : Object.fromEntries(
            Object.entries(previous).filter(([id]) => pendingIds.has(id)),
          ),
    );
    setPaused((previous) =>
      Object.keys(previous).every((id) => pendingIds.has(id))
        ? previous
        : Object.fromEntries(
            Object.entries(previous).filter(([id]) => pendingIds.has(id)),
          ),
    );
  }, [pending]);
  return (
    <section
      id="order-edit-requests"
      className={cardClass}
      aria-labelledby="requests-heading"
    >
      <h2 id="requests-heading" className="text-base font-semibold">
        Requests awaiting review
      </h2>
      <p className="mt-2 text-sm text-gray-600">
        Approval authorizes a proposal for the customer to confirm while it
        remains eligible. Reviewing a request does not execute an edit or charge
        the merchant.
      </p>
      {!pending.length ? (
        <p className="mt-4 text-sm text-gray-500">
          No requests awaiting review.
        </p>
      ) : (
        <div className="mt-4 space-y-4">
          {pending.map((request) => {
            const order = request.orderId.split('/').pop();
            const shippingPaused = request.action === 'shipping';
            return (
              <article
                key={request.id}
                className="rounded-md border border-gray-200 p-4"
              >
                <h3 className="text-sm font-medium">
                  {actionLabel(request.action)} · Order {order}
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Requested {displayDate(request.createdAt)}
                  {request.action !== 'support' && (
                    <> · Expires {displayDate(request.expiresAt)}</>
                  )}
                </p>
                <p className="mt-3 text-sm">{request.summary}</p>
                {shippingPaused && (
                  <p className="mt-3 text-sm text-amber-900">
                    Shipping Offers is paused. You can reject this request.
                  </p>
                )}
                {request.quote && (
                  <div className="mt-3 rounded bg-gray-50 p-3">
                    <ChangeSummary value={request.quote} />
                  </div>
                )}
                <label className="mt-3 block text-sm">
                  Review note for order {order}
                  <textarea
                    className={inputClass}
                    maxLength={500}
                    rows={2}
                    value={notes[request.id] || ''}
                    onChange={(event) =>
                      setNotes({ ...notes, [request.id]: event.target.value })
                    }
                    disabled={busy}
                  />
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {!shippingPaused && request.requiresFulfillmentPause && (
                    <label className="flex w-full items-start gap-2 rounded-md bg-amber-50 p-3 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1 rounded border-gray-300"
                        checked={paused[request.id] === true}
                        disabled={busy}
                        onChange={(event) =>
                          setPaused({
                            ...paused,
                            [request.id]: event.target.checked,
                          })
                        }
                      />
                      Fulfillment is paused with our warehouse or fulfillment
                      team until payment and this change are confirmed
                    </label>
                  )}
                  <Button
                    disabled={
                      busy ||
                      shippingPaused ||
                      (request.requiresFulfillmentPause === true &&
                        !paused[request.id])
                    }
                    isLoading={reviewing === request.id}
                    onClick={() => {
                      void onReview(
                        request.id,
                        'approve',
                        notes[request.id] || '',
                        request.requiresFulfillmentPause
                          ? paused[request.id] === true
                          : undefined,
                      );
                    }}
                    props={{
                      'aria-label': `${
                        request.action === 'support'
                          ? 'Resolve support request'
                          : 'Approve request'
                      } for order ${order}`,
                    }}
                  >
                    {request.action === 'support'
                      ? 'Mark resolved'
                      : 'Approve proposal'}
                  </Button>
                  {request.action !== 'support' && (
                    <Button
                      variant="outlined"
                      disabled={busy}
                      onClick={() => {
                        void onReview(
                          request.id,
                          'reject',
                          notes[request.id] || '',
                        );
                      }}
                      props={{
                        'aria-label': `Reject request for order ${order}`,
                      }}
                    >
                      Reject
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function OperationalAnalytics({
  analytics,
}: {
  analytics?: OrderEditingAnalytics;
}) {
  const metrics = [
    ['Observed eligible orders', analytics?.eligibleOrders],
    ['Orders with attempts', analytics?.attemptedOrders],
    ['Orders with completed edits', analytics?.completedOrders],
    ['Failed operations', analytics?.failedOperations],
    ['Pending operations', analytics?.pendingOperations],
    [
      'Editing revenue',
      analytics?.editingRevenueCents === null ||
      analytics?.editingRevenueCents === undefined
        ? null
        : money(analytics.editingRevenueCents),
    ],
    ['Support requests', analytics?.supportRequests],
    ['Recorded retention choices', analytics?.retainedCancellations],
  ] as const;
  return (
    <section
      aria-label="Operational analytics"
      className="mt-6 border-t border-gray-200 pt-5"
    >
      <h3 className="text-sm font-semibold">Operational analytics</h3>
      <p className="mt-2 text-xs leading-5 text-gray-500">
        Counts use the reporting period above. Eligibility counts reflect
        authenticated observations; unmeasured metrics remain unavailable. An
        edited order is not a measured support ticket avoided.
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        {metrics.map(([label, value]) => (
          <div key={label} className="rounded-md bg-gray-50 p-3">
            <dt className="text-xs text-gray-600">{label}</dt>
            <dd className="mt-1 text-lg font-semibold">
              {value === null || value === undefined ? 'Not measured' : value}
            </dd>
          </div>
        ))}
      </dl>
      <div className="mt-4 grid gap-5 md:grid-cols-2">
        <div>
          <h4 className="text-sm font-medium">By action</h4>
          {!Array.isArray(analytics?.byAction) ? (
            <p className="mt-2 text-xs text-gray-500">
              Action breakdown unavailable.
            </p>
          ) : analytics?.byAction.length ? (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr>
                    <th className="py-2">Action</th>
                    <th>Attempts</th>
                    <th>Completed</th>
                    <th>Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.byAction.map((row) => (
                    <tr key={row.action}>
                      <td className="py-2">{actionLabel(row.action)}</td>
                      <td>{row.attempted}</td>
                      <td>{row.completed}</td>
                      <td>{row.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-2 text-xs text-gray-500">
              No action breakdown recorded.
            </p>
          )}
        </div>
        <div>
          <h4 className="text-sm font-medium">Failure reasons</h4>
          {!Array.isArray(analytics?.failureReasons) ? (
            <p className="mt-2 text-xs text-gray-500">
              Failure reasons unavailable.
            </p>
          ) : analytics?.failureReasons.length ? (
            <ul className="mt-2 space-y-2 text-xs">
              {analytics.failureReasons.map((row) => (
                <li key={row.code}>
                  {row.code.replace(/_/g, ' ').toLowerCase()} · {row.count}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-gray-500">
              No failure reasons recorded.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
